/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useState } from 'react';

import { cssMap as cssMapCompiled, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl-next';

import Checkbox from '@atlaskit/checkbox';
import { Field } from '@atlaskit/form';
import { fg } from '@atlaskit/platform-feature-flags';
import { Anchor, Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { TeamsAnchor } from '@atlaskit/teams-app-internal-navigation';
import { token } from '@atlaskit/tokens';

import { TeamMembershipSettings } from '../../types';
import { messages } from '../i18n/messages';

const compiledStyles = cssMapCompiled({
	checkboxLine: {
		display: 'flex',
		alignItems: 'center',
	},
	openMembershipDescriptionAnchor: {
		display: 'contents',
	},
	openMembershipDescription: {
		marginInlineStart: token('space.050'),
	},
});

interface TeamTypePickerProps {
	defaultValue?: TeamMembershipSettings;
	onChange: (newValue: TeamMembershipSettings) => void;
}

/**
 * !!It is actually MEMBERSHIP CONTROL, not TEAM TYPE PICKER!!
 */
export const TeamTypePicker = ({
	defaultValue,
	onChange,
}: TeamTypePickerProps): React.JSX.Element => {
	const { fireEvent } = useAnalyticsEvents();
	const [isMembershipOpen, setIsMembershipOpen] = useState(
		defaultValue === TeamMembershipSettings.OPEN,
	);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const chosenValue = event.currentTarget.checked
				? TeamMembershipSettings.OPEN
				: TeamMembershipSettings.INVITE_ONLY;

			fireEvent('ui.teamTypePicker.clicked', {
				defaultType: defaultValue || null,
				chosenType: chosenValue,
			});

			setIsMembershipOpen(chosenValue === TeamMembershipSettings.OPEN);
			onChange(chosenValue);
		},
		[defaultValue, onChange, fireEvent],
	);

	const label = (
		<Text size="small">
			<FormattedMessage {...messages.teamTypeCheckboxLabel} />
		</Text>
	);

	return (
		<Stack space="space.050">
			<Field name="teamType" label={<FormattedMessage {...messages.teamTypePickerLabel} />}>
				{() => (
					<div css={compiledStyles.checkboxLine}>
						<Checkbox
							name="teamType"
							defaultChecked={defaultValue === TeamMembershipSettings.OPEN}
							onChange={handleChange}
							label={label}
						/>
					</div>
				)}
			</Field>
			{isMembershipOpen && (
				<Box xcss={compiledStyles.openMembershipDescription}>
					<Text color="color.text.subtlest" size="small">
						<FormattedMessage
							{...messages.teamOpenMembershipDisclaimer}
							values={{
								a: (chunks) =>
									fg('ptc-links-migrate-atlaskit-anchor-to-teams-anchor') ? (
										<TeamsAnchor
											href="https://support.atlassian.com/platform-experiences/docs/start-an-atlassian-team/#Understanding-membership-controls"
											intent="reference"
											xcss={compiledStyles.openMembershipDescriptionAnchor}
										>
											{chunks}
										</TeamsAnchor>
									) : (
										<Anchor
											href="https://support.atlassian.com/platform-experiences/docs/start-an-atlassian-team/#Understanding-membership-controls"
											target="_blank"
											rel="noopener noreferrer"
											xcss={compiledStyles.openMembershipDescriptionAnchor}
										>
											{chunks}
										</Anchor>
									),
							}}
						/>
					</Text>
				</Box>
			)}
		</Stack>
	);
};

export default TeamTypePicker;
